(function(angular, _) {
    'use strict';

    angular.module('wtApp.dataService')
        .factory('WtAnalysis', ['$injector', function($injector) {
            const $q = $injector.get('$q');
            const $log = $injector.get('$log');
            const Monads = $injector.get('Monads');
            const Columns = $injector.get('Columns');
            const Filters = $injector.get('Filters');
            const Analyses = $injector.get('Analyses');
            const ViewMode = $injector.get('ViewMode');
            const Variants = $injector.get('Variants');
            const jobStatus = $injector.get('jobStatus');
            const TimeSeries = $injector.get('TimeSeries');
            const cohortUtils = $injector.get('cohortUtils');
            const filterUtils = $injector.get('filterUtils');
            const WtObservable = $injector.get('WtObservable');
            const viewModePath = $injector.get('viewModePath');
            const emptyResponse = $injector.get('emptyResponse');
            const accountService = $injector.get('accountService');
            const segmentService = $injector.get('segmentService');
            const requestService = $injector.get('requestService');
            const trackingService = $injector.get('trackingService');
            const timeShiftService = $injector.get('timeShiftService');
            const WtAnalysisColumns = $injector.get('WtAnalysisColumns');
            const viewModeComparison = $injector.get('viewModeComparison');
            const WtAnalysisViewModes = $injector.get('WtAnalysisViewModes');
            const requestObjectFactory = $injector.get('requestObjectFactory');
            const debugEnabled = $injector.get('debugEnabled');

            /**
             * @ngdoc service
             * @name WtAnalysis
             * @description The abstraction layer for analysis factories
             * @todo Add analysis schema validation based on JOI https://github.com/hapijs/joi
             */
            return class WtAnalysis {
                /**
                 * @ngdoc property
                 * @name WtAnalysis.MANDATORY_FIELDS
                 * @description Mandatory fields as defined by the service
                 * @returns {Array}
                 * @static
                 */
                static get MANDATORY_FIELDS() {
                    return [
                        'id',
                        'title',
                        'columns',
                        'variant',
                        'showTable',
                        'showPic',
                        'forbidChart'
                    ];
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis.CHART_TYPES
                 * @description A map of backend/frontend valid values for the analysis standardChart value
                 * @returns {Object}
                 * @static
                 */
                static get CHART_TYPES() {
                    return {
                        COLUMN: 'column',
                        BAR: 'bar',
                        LINE: 'line',
                        PIE: 'pie',
                        AREA: 'area',
                        PYRAMID_2D: 'pyramid2D',
                        FUNNEL_2D: 'funnel2D',
                        CLOUD: 'cloud',
                        GERMANY_MAP: 'germanyMap',
                        WORLD_MAP: 'worldMap',
                        STACKED_COLUMN: 'stackedColumn',
                        STACKED_COLUMN_ABSOLUTE: 'stackedColumnAbsolute',
                        PROCESS: 'process',
                        SANKEY: 'sankey'
                    };
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis.VARIANTS
                 * @description Map of supported valid values for analysis variant
                 * @returns {Object}
                 * @static
                 */
                static get VARIANTS() {
                    return {
                        LIST: 'LIST',
                        PIVOT: 'PIVOT',
                        PIVOT_AS_LIST: 'PIVOT_AS_LIST'
                    };
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis.ACTIONS
                 * @description A map of actions that can be emitted by the analysis "changes" observable
                 * @returns {Object}
                 * @static
                 */
                static get ACTIONS() {
                    return {
                        /**
                         * Triggered whenever a property on the analysis configuration has changed.
                         */
                        PROPERTY_CHANGE: 'PROPERTY_CHANGE',

                        /**
                         * Triggered whenever the session time filter associated with the analysis changes.
                         */
                        SESSION_TIME_FILTER_CHANGE: 'SESSION_TIME_FILTER_CHANGE',

                        /**
                         * Triggered on analysis variant change
                         */
                        VARIANT_CHANGE: 'VARIANT_CHANGE',

                        /**
                         * Triggered whenever the analysis configuration has changed which requires a new request to be fired
                         */
                        TRIGGER_ANALYSIS_REQUEST: 'TRIGGER_ANALYSIS_REQUEST',

                        /**
                         * Triggered when segment connections or container changed
                         */
                        FILTERS_CONFIG_CHANGE: 'FILTERS_CONFIG_CHANGE',

                        /**
                         * Triggered when configuration of the analysis view mode has changed
                         * TODO: VIEW_MODE
                         */
                        VIEW_MODE_CONFIGURATION_CHANGE: 'VIEW_MODE_CONFIGURATION_CHANGE',

                        /**
                         * Triggered when view mode has switched
                         * TODO: VIEW_MODE
                         */
                        VIEW_MODE_SWITCH: 'VIEW_MODE_SWITCH',

                        /**
                         * Triggered when edit state has switched
                         * TODO: VIEW_MODE
                         */
                        EDIT_STATE_SWITCH: 'EDIT_STATE_SWITCH',

                        /**
                         * Triggered when config is reset
                         */
                        CONFIG_RESET: 'CONFIG_RESET',

                        /**
                         * Triggered when the table visibility has changed
                         */
                        TABLE_VISIBILITY_CHANGED: 'TABLE_VISIBILITY_CHANGED',

                        /**
                         * Triggered when the chart visibility has changed
                         */
                        CHART_VISIBILITY_CHANGED: 'CHART_VISIBILITY_CHANGED',

                        /**
                         * Triggered when the empty response is generated
                         */
                        EMPTY_RESPONSE_GENERATED: 'EMPTY_RESPONSE_GENERATED',

                        /**
                         * Triggered when the request lock mechanism was toggled
                         */
                        REQUEST_LOCK_CHANGE: 'REQUEST_LOCK_CHANGE'
                    };
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis.validate
                 * @description Validates an analysis
                 * @param {Object} config Analysis configuration
                 * @throws {Error} Invalid argument exception
                 *
                 */
                static validate(config) {
                    if (!_.isObject(config)) {
                        throw Error('Invalid analysis input argument');
                    }

                    _.forEach(WtAnalysis.MANDATORY_FIELDS, function(iFieldName) {
                        if (!_.hasIn(config, iFieldName)) {
                            throw Error('Missing the mandatory field "' + iFieldName + '"');
                        }
                    });

                    if (!_.isArray(config.columns)) {
                        throw Error('Invalid analysis columns');
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#constructor
                 * @description WtAnalysis constructor
                 * @param {Object} config The analysis configuration object
                 * @throws {Error} Invalid argument exception
                 */
                constructor(config) {
                    /**
                     * Allows the implementation class to extend the validation logic
                     * Older browsers do not support the proto constructor property which fail the entire application.
                     * Skip validation for such browsers until the time comes and we drop their support
                     */
                    if (_.isFunction(_.get(this, 'constructor.validate'))) {
                        this.constructor.validate(config);
                    }

                    /**
                     * Flag that enables model's debug info to be printed to the console
                     * @type {boolean}
                     */
                    this.debugEnabled = debugEnabled;

                    /**
                     * Flag that indicates whether the analysis is configured to lock requests or not.
                     * @type {boolean}
                     * @private
                     */
                    this.requestLock_ = false;

                    /**
                     * Flag that indicates whether the request lock is activated or not.
                     * @type {boolean}
                     * @private
                     */
                    this.isRequestLockActivated_ = false;

                    /**
                     * Holds the original viewMode configurations; before resolving and correcting.
                     * @type {Object}
                     * @private
                     */
                    this.viewModeOrigin_ = _.chain(config).get('viewMode').cloneDeep().valueOf();

                    /**
                     * Holds the original data
                     * @type {Object}
                     * @private
                     */
                    this.pristineData_ = _.cloneDeep(ViewMode.resolveViewMode(config));

                    /**
                     * Holds the current data
                     * Note that it is returned as a reference which can be modified externally
                     * The direction of development should be that we use immutable data structures here
                     * @type {Object}
                     * @private
                     */
                    this.data_ = _.cloneDeep(ViewMode.resolveViewMode(config));

                    /**
                     * The data layer the analysis operates on
                     * @type {string}
                     */
                    this.layer_ = this.get('realTime') ? Monads.REAL_TIME : Monads.RAW_DATA;

                    /**
                     * The instance of the WtAnalysisColumns responsible for managing the columns of the analysis
                     * All operations on the columns should be performed via this property, no operation is allowed on the columns
                     * configuration of the analysis.
                     * The outer world can access the columns instance via the usual instance getter `this.get('columns')`
                     * in which case it will receive the columns instance and not the original configuration of the columns
                     * @type {null|WtAnalysisColumns}
                     * @private
                     */
                    this.columns_ = null;

                    /**
                     * The instance of WtViewModes responsible for managing the viewModes property of the analysis, eg. TimeSeries, Comparison, etc.
                     * @type {null|WtAnalysisViewModes}
                     * @private
                     */
                    this.viewModes_ = null;

                    /**
                     * Meta property of the analysis holding the time filter
                     * The time filter is not a persisted value of an analysis, it is rather a meta data structure that can be used in some edge cases when the exact snapshot of the analysis is required
                     * @type {null}
                     * @private
                     */
                    this.timeFilter_ = null;

                    /**
                     * Meta property of the analysis indicating if a view mode has been activated at least once. It is used to handle the 'back' button behaviour.
                     * @type {Object}
                     * @private
                     */
                    this.activeViewModes_ = {};

                    /**
                     * The observer object that can be used to push changes into the observable
                     * @example
                     *  - Notify the listeners about a new value: this.changesEmitter_.next('value');
                     *  - Notify the listeners about an error: this.changesEmitter_.error(new Error('Something went wrong'))
                     *  - Notify the listeners about the completion: this.changesEmitter_.complete()
                     * @type {null|Object}
                     * @private
                     */
                    this.changesEmitter_ = null;

                    /**
                     * The observable object that can be subscribed to listen to the changes on the analysis instance
                     * @example
                     *  const subscription = analysis.changes.subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the analysis change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @type {WtObservable}
                     */
                    this.changes = new WtObservable((observer) => {
                        this.changesEmitter_ = observer;
                    });

                    /**
                     * Enhances the analysis.changes observable by adding a filter to be able to subscribe to specific action(s)
                     * @param {string|Array} actionType One or more action types to subscribe to
                     * @example
                     *  const subscription = analysis.changesOfType('PROPERTY_CHANGE').subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the analysis change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @returns {WtObservable}
                     */
                    this.changesOfType = function(actionType) {
                        return this.changes
                            .filter((action) => _.includes(_.isArray(actionType) ? actionType : [actionType], _.get(action, 'type')));
                    };

                    /**
                     * Enhances the analysis.changes observable of 'PROPERTY_CHANGE' by adding a filter to be able to subscribe to changes
                     * of specific properties - providing a list of property path prefixes can also work
                     * @param {string|string[]} paths_ A config path or a list of paths
                     * @example
                     *  const subscription = analysis.changesInPath('standardChart').subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the analysis change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @returns {WtObservable}
                     */
                    this.changesInPath = function(paths_) {
                        return this.changesOfType('PROPERTY_CHANGE')
                                .filter((action) => {
                                    const paths = _.isArray(paths_) ? paths_ : [paths_];
                                    const path = _.get(action, 'payload.path');
                                    return _.chain(paths)
                                            .map((path_) => _.startsWith(`${path}`, `${path_}`))
                                            .includes(true)
                                            .value();
                                });
                    };

                    /**
                     * List of analysis requests
                     * @type {Array<AnalysisRequest>}
                     * @private
                     */
                    this.requests_ = [];

                    /**
                     * Holds the latest response for this analysis
                     * @type {Object|null}
                     * @private
                     */
                    this.latestResponse_ = null;

                    /**
                     * List of actions that mark the analysis for a scheduled request
                     * The analysis collects all the changes that require the data reload and triggers a new request in the next event loop tick
                     * It should be noted that when two changes requiring data reload are triggered in an async manner,
                     * it will generate two different requests
                     * @type {Array}
                     */
                    this.analysisRequestTriggers = [];

                    /**
                     * Path to menu item related to an analysis
                     * @type {*}
                     * @private
                     */
                    this.menuPath_ = [];

                    this.init_();
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#pristineConfig
                 * @description Exposes the pristine analysis configuration object to the outer world.
                 * Note that for analyses that hide the analysis configuration under configJson (report analyses, KPIs, Sparklines), the configJson value is returned by this property
                 * @returns {Object}
                 */
                get pristineConfig() {
                    return this.pristineData_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#config
                 * @description Exposes the analysis configuration object to the outer world
                 * Note that for analyses that hide the analysis configuration under configJson (report analyses, KPIs, Sparklines), the configJson value is returned by this property
                 * @returns {Object}
                 */
                get config() {
                    return this.data_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#columns
                 * @description Exposes the analysis columns instance
                 * @returns {Object}
                 */
                get columns() {
                    return this.columns_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#viewModes
                 * @description Exposes the analysis viewModes instance
                 * @returns {Object}
                 */
                get viewModes() {
                    return this.viewModes_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#layer
                 * @description The layer the analysis operate on (the regular analyses and live analyses work on separate column definitions)
                 * @returns {string}
                 */
                get layer() {
                    return this.layer_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#firstDimensionTimeFormat
                 * @description The timeFormat of the first dimension of the analysis
                 * @returns {string}
                 */
                get firstDimensionTimeFormat() {
                    return _.chain(this.columns.getDimensions()).first().get('timeFormat', '').value();
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#defaultSumValue
                 * @description The default sumValue to set on columns of analyses. Depends on the layer of an analysis:
                 * (1) Real time analyses default to sumValue: true; so as to get single values. sumValue: false will return comma separated value strings
                 * (2) Non real time analyses default to sumValue: false, with no visible outcome - most possibly due to performance.
                 * @returns {boolean}
                 */
                get defaultSumValue() {
                    return Boolean(this.get('realTime'));
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#timeSeriesViewStateLoadedOnce
                 * @description Indicates that the analysis instance has been loaded in time series view state mode at least once.
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                get timeSeriesViewStateLoadedOnce() {
                    return this.activeViewModes_[ViewMode.VIEW_MODES.TIME_SERIES];
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#isPathApplicable
                 * @description Indicates whether the analysis can have path view mode or not
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                get isPathApplicable() {
                    return viewModePath.canHavePath(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#hasPathCompatibleDimension
                 * @description Indicates whether the analysis has one path compatible dimension.
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                get hasPathCompatibleDimension() {
                    return viewModePath.hasPathCompatibleDimension(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#isTimeSeriesApplicable
                 * @description Indicates whether the analysis can have time series view mode or not
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                get isTimeSeriesApplicable() {
                    return TimeSeries.canHaveTimeSeries(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#isComparisonApplicable
                 * @description Indicates whether the analysis can have comparison view mode or not
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                get isComparisonApplicable() {
                    return viewModeComparison.canHaveComparison(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#xomparisonTimeShifts
                 * @description Returns the list of time shifts
                 * @returns {Array<{timeFormat: string, timeShiftBy: number, timeShiftUnit: string}>}
                 * TODO: VIEW_MODE
                 */
                get comparisonTimeShifts() {
                    return viewModeComparison.getComparisonTimeShifts(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#actions
                 * @description Exposes the possible actions map to be used by the columns changes consumers
                 * @returns {Object}
                 */
                get actions() {
                    return WtAnalysis.ACTIONS;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#chartTypes
                 * @description Returns an object with frontend chart types mapped to backend values
                 * @returns {Object}
                 */
                get chartTypes() {
                    return WtAnalysis.CHART_TYPES;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#hasRequestLock
                 * @description Determines the current state of the request lock feature for this analysis
                 * @returns {boolean}
                 */
                get hasRequestLock() {
                    return this.requestLock_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#menuPath
                 * @description A clone of the private menuPath_
                 * @returns {*}
                 */
                get menuPath() {
                    return _.cloneDeep(this.menuPath_);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#numberColumnVisible
                 * @description Returns the numberColumnVisible property of an analysis defaulting to false if undefined.
                 * @returns {boolean}
                 */
                get numberColumnVisible() {
                    return this.get('numberColumnVisible') || false;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#latestResponse
                 * @description The latest response containing actual data
                 * @returns {Object|null}
                 */
                get latestResponse() {
                    return _.cloneDeep(this.latestResponse_);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#isRequestLockActivated
                 * @description Determines if the request lock is actived or not
                 * @returns {boolean}
                 */
                get isRequestLockActivated() {
                    return this.isRequestLockActivated_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#isRequestLockApplicable_
                 * @description Determines whether the request lock can be activated
                 * @returns {boolean}
                 * @private
                 */
                get isRequestLockApplicable_() {
                    return this.requestLock_ && !this.isTimeSeriesViewMode() && !this.isRequestLockActivated;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#canBuildDummyResponse
                 * @description Determines which analyses have a dummy response
                 * @returns {boolean}
                 */
                get canBuildDummyResponse() {
                    const isNotPageDetail = this.get('id') !== 'page_detail';
                    return !this.isVideoEngagementAnalysis() && isNotPageDetail && !this.get('realTime');
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#latestRequest
                 * @description Returns the analysis request associated with this analysis
                 * @returns {Object}
                 */
                get latestRequest() {
                    return _.chain(this)
                        .get('requests_', [])
                        .last()
                        .value();
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#latestEmptyResponse
                 * @description Returns the empty response hash that
                 * corresponds to the latest analysis request from the request list
                 * @returns {Object}
                 */
                get latestEmptyResponse() {
                    return _.get(this.latestRequest, 'emptyResponse', null);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#isJobInitializing
                 * @description Determines if the latest analysis request has concluded or not
                 * @returns {boolean}
                 */
                get isJobInitializing() {
                    const status = _.get(this.latestRequest, 'status', null);
                    return jobStatus.isJobInitializing(status);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#isJobRunning
                 * @description Determines if the latest analysis request is still running on the main
                 * server side.
                 * @returns {boolean}
                 */
                get isJobRunning() {
                    const status = _.get(this.latestRequest, 'status', null);
                    return jobStatus.isJobRunning(status);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#timeSeriesTimeArrangeType
                 * @description The time arrange type that will be used for the time series mode
                 * @returns {string}
                 */
                get timeSeriesTimeArrangeType() {
                    return TimeSeries.getDefaultTimeArrangeType(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#canHaveManyDimensions
                 * @description Determines is the analysis can have more than one dimension columns
                 * @returns {boolean}
                 */
                get canHaveManyDimensions() {
                    return Analyses.canHaveManyDimensions(this.config);
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#sessionLimits
                 * @description The limits of the first dimension [lowerLimit, upperLimit]
                 * @returns {Array}
                 */
                get sessionLimits() {
                    return this.columns.getSessionLimits();
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#columnConfig
                 * @description Proxies the columns config getter
                 * @returns {Array}
                 */
                get columnConfig() {
                    return this.columns.config;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#resolvedViewModes
                 * @description Returns a clone of the complere re-resolved viewMode configuration object.
                 * @returns {Object}
                 */
                get resolvedViewModes() {
                    const config = ViewMode.resolveViewMode(_.cloneDeep(this.config));
                    return _.get(config, 'viewMode');
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis#resolvedViewMode
                 * @description Returns a clone of the re-resolved viewMode configuration object for the currently active view mode.
                 * @returns {Object}
                 */
                get resolvedViewMode() {
                    return _.get(this.resolvedViewModes, `${this.getActiveViewMode()}`);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getTimeSeriesMetricAliases_
                 * @description Returns a list of metric aliases as they will be requested for the current comparison analysis.
                 * List can be narrowed down to metrics visible in chart on demand.
                 * @param   {boolean=} visibleInChart Determines if chart only visible aliases are to be returned or not
                 * @returns {Array<string>}
                 * @private
                 * TODO: VIEW_MODE
                 */
                getComparisonMetricAliases_(visibleInChart = false) {
                    let aliasGroups = this.getComparisonAliasGroups();

                    if (visibleInChart) {
                        const aliasesInChart = _.map(this.columns.getChartVisibleColumns(), 'alias');
                        const dropAliasesBefore = this.viewModes.comparison.activeComparisonValues.length;
                        aliasGroups = _.chain(aliasGroups)
                                        .pickBy((aliasGroup, parentAlias) => _.includes(aliasesInChart, parentAlias))
                                        .forEach(aliasGroup => _.remove(aliasGroup, (alias, index) => dropAliasesBefore > index))
                                        .value();
                    }

                    return _.flatMap(aliasGroups);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getTimeSeriesMetricAliases_
                 * @description Returns a list of metric aliases as they will be requested for the current time series analysis.
                 * List can be narrowed down to metrics visible in chart on demand.
                 * @param   {boolean=} visibleInChart Determines if chart only visible aliases are to be returned or not
                 * @returns {Array<string>}
                 * @private
                 * TODO: VIEW_MODE
                 */
                getTimeSeriesMetricAliases_(visibleInChart = false) {
                    let aliasGroups = this.getTimeSeriesAliasGroups();

                    if (visibleInChart) {
                        const aliasesInChart = _.map(this.columns.getTimeSeriesChartVisibleColumns(), 'alias');
                        aliasGroups = _.pickBy(aliasGroups, (aliasGroup, parentAlias) => _.includes(aliasesInChart, parentAlias));
                    }

                    return _.flatMap(aliasGroups);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getAliases
                 * @description Returns a list of all visible aliases of the current analysis
                 * @returns {Array<string>}
                 */
                getAliases() {
                    const dimensionAliasese = _.map(this.columns.getDimensions(), 'alias');
                    const metricAliases = this.getMetricAliases();
                    return [...dimensionAliasese, ...metricAliases];
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getMetricTitlesInChart
                 * @description Will return the list of the metric titles that are visible in the chart
                 * @returns {Array<string>}
                 */
                getMetricTitlesInChart() {
                    return _.map(this.getMetricAliases(true), metricAlias => {
                        let alias = metricAlias;

                        if (this.isComparisonViewMode()) {
                            alias = this.getComparisonParentAlias(metricAlias);
                        }

                        if (this.isTimeSeriesViewState()) {
                            alias = this.getTimeSeriesParentAlias(metricAlias);
                        }

                        const column = this.columns.getColumn(alias);

                        return _.get(column, 'title', '');
                    });
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getMetricAliases
                 * @description Returns a list of metric aliases as they will be requested for the current analysis, with
                 * respect to their current view mode. List can be narrowed down to metrics visible in chart on demand.
                 * @param   {boolean=} visibleInChart Determines if chart only visible aliases are to be returned or not
                 * @returns {Array<string>}
                 */
                getMetricAliases(visibleInChart = false) {
                    if (this.isPathViewMode()) {
                        return [`${this.viewModes.path.metricAlias}`];
                    }

                    if (this.isComparisonViewMode()) {
                        return this.getComparisonMetricAliases_(visibleInChart);
                    }

                    if (this.isTimeSeriesViewState()) {
                        return this.getTimeSeriesMetricAliases_(visibleInChart);
                    }

                    const metrics = visibleInChart ? this.columns.getChartVisibleColumns() : this.columns.getMetrics();

                    return _.map(metrics, 'alias');
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setMenuPath
                 * @description Setter
                 * @param   {*} menuPath
                 * @returns {WtAnalysis}
                 */
                setMenuPath(menuPath) {
                    this.menuPath_ = menuPath;
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setLatestResponse
                 * @description Plain setter
                 * @param {Object} latestResponse
                 * @returns {WtAnalysis}
                 */
                setLatestResponse(latestResponse) {
                    this.latestResponse_ = _.cloneDeep(latestResponse);
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#lockRequests
                 * @description Sets the requestLock_ 'feature' property
                 * @param {boolean} requestLock
                 * @returns {WtAnalysis}
                 */
                lockRequests(requestLock) {
                    this.requestLock_ = requestLock;
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#activateRequestLock
                 * @description Activates the request lock if the 'feature' is enabled
                 * @returns {WtAnalysis}
                 */
                activateRequestLock() {
                    if (this.isRequestLockApplicable_) {
                        const previousValue = this.isRequestLockActivated_;
                        const currentValue = true;
                        this.isRequestLockActivated_ = true;

                        if (currentValue !== previousValue) {
                            this.dispatchAction(this.actions.REQUEST_LOCK_CHANGE, {currentValue, previousValue});
                        }

                        this.abortRequests();
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#deactivateRequestLock
                 * @description Deactivates the request lock if the 'feature' is enabled
                 * @returns {WtAnalysis}
                 */
                deactivateRequestLock() {
                    if (this.requestLock_) {
                        const previousValue = this.isRequestLockActivated_;
                        const currentValue = false;

                        this.isRequestLockActivated_ = false;

                        if (currentValue !== previousValue) {
                            this.dispatchAction(this.actions.REQUEST_LOCK_CHANGE, {currentValue, previousValue});
                        }
                    }
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#triggerAnalysisRequest
                 * @description Triggers analysis requests if no requests are not locked
                 * @returns {WtAnalysis}
                 */
                triggerAnalysisRequest() {
                    if (this.isRequestLockActivated) {
                        return this;
                    }
                    const reload = false;
                    const triggers = this.analysisRequestTriggers;
                    this.dispatchAction('TRIGGER_ANALYSIS_REQUEST', {reload, triggers});
                    this.analysisRequestTriggers = [];
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#abortRequests
                 * @description Will abort all running requests for an analysis
                 * @returns {WtAnalysis}
                 */
                abortRequests() {
                    _.chain(this.requests_)
                        .remove()
                        .filter(({status}) => jobStatus.isAbortable(status))
                        .forEach(previousRequest => requestService.cancelAnalysisRequest(previousRequest))
                        .value();

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#buildEmptyResponse
                 * @description Will build an empty response based on the current snapshot.
                 * @returns {Promise}
                 */
                buildEmptyResponse() {
                    return this.buildDummyResponse_(this.getConfigClone(null, true));
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#buildDummyResponse_
                 * @description Builds a dummy response based on an analysis configuration
                 * and notify subscribers.
                 * @param   {Object} analysisConfigSnapshot
                 * @returns {Promise}
                 * @private
                 */
                buildDummyResponse_(analysisConfigSnapshot) {
                    return emptyResponse
                            .buildBeforeQuery(this.get('id'), analysisConfigSnapshot)
                            .then((response) => {
                                this.dispatchAction(WtAnalysis.ACTIONS.EMPTY_RESPONSE_GENERATED, {emptyResponse: response});
                                return response;
                            })
                            .catch(error => $log.error(error));
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#buildDummyResponseFromRequest
                 * @description Builds a dummy response and append it to the analysis request.
                 * @param   {AnalysisRequest} analysisRequest
                 * @returns {Promise}
                 * @private
                 */
                buildDummyResponseFromRequest(analysisRequest) {
                    const isDataOnlyRequest = (analysisRequest.resultType === 'DATA_ONLY');

                    if (!this.canBuildDummyResponse || !isDataOnlyRequest) {
                        return $q.reject(new Error('Failed to construct an empty response for the current analysis'));
                    }

                    return this.buildDummyResponse_(analysisRequest.snapshot)
                       .then(response => {
                           analysisRequest.setEmptyResponse(response);

                           return response;
                       })
                       .catch(angular.noop);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isLatestRequest
                 * @description Determines if the given analysisRequest instance is the latest for this analysis instance.
                 * @param   {AnalysisRequest}  analysisRequest
                 * @returns {boolean}
                 */
                isLatestRequest(analysisRequest) {
                    return _.isEqual(this.latestRequest, analysisRequest);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#addRequest
                 * @description Pushes a new request for this analysis instance in the list of requests
                 * while cancelling all previous ones
                 * @param   {AnalysisRequest} analysisRequest
                 * @returns {WtAnalysis}
                 */
                addRequest(analysisRequest) {
                    analysisRequest.setSnapshot(this.getConfigClone(null, true));

                    this.abortRequests().requests_.push(analysisRequest);

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#init_
                 * @description Initialization phase of the instance
                 * @returns {WtAnalysis}
                 */
                init_() {
                    this.initPredefinedContainer_();
                    this.initPredefineSegment_();
                    this.initColumns_();
                    this.initViewModes_();

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#initPredefinedContainer_
                 * @description Initializes the predefinedContainer property of the analysis which is responsible for the definition of filters on analysis level
                 * @returns {WtAnalysis}
                 * @private
                 */
                initPredefinedContainer_() {
                    const predefinedContainer = this.get('predefinedContainer');
                    const containerStructure = {
                        /**
                         * Combined flag indicates ...
                         * @type {boolean}
                         */
                        combined: false,

                        /**
                         * The list of filters (except segments) from the auto container. The segments are stored under the predefinedSegmentConnections
                         * @type {Array}
                         */
                        filters: [],

                        /**
                         * The list of containers except the auto container, whose filters are listed under filters property
                         * @type {Array}
                         */
                        containers: []
                    };
                    const isInit = _.reduce(_.keys(containerStructure), (acc, key) => acc && _.has(predefinedContainer, key), true);

                    if (!isInit) {
                        this.setContainers_(_.extend(containerStructure, predefinedContainer));
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#initPredefineSegment_
                 * @description Initializes the predefinedSegmentConnections property of the analysis that is responsible for the definition of segments used in the analysis level filters
                 * @returns {WtAnalysis}
                 * @private
                 */
                initPredefineSegment_() {
                    const predefinedSegmentConnections = this.get('predefinedSegmentConnections');

                    if (!_.isArray(predefinedSegmentConnections)) {
                        this.setSegmentConnections_(_.extend([], predefinedSegmentConnections));
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#initColumns_
                 * @description Initializes the columns instance responsible for managing the analysis columns.
                 * @returns {WtAnalysis}
                 * @private
                 */
                initColumns_() {
                    const columns = _.get(this.config, 'columns');

                    // Initialize the columns instance only when the analysis has columns.
                    // The inheriting analyses can override this method if the configuration differs from the regular analysis (eg. KPI, Sparkline)
                    if (_.isArray(columns) && _.size(columns)) {
                        this.columns_ = new WtAnalysisColumns(columns, this);

                        this.columnsSubscription = this.columns.changes.subscribe(this.onColumnsChange_.bind(this));
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#onColumnsChange_
                 * @description Columns changes observable subscriber. Proxies the columns changes as its own. Relies on the columns change
                 * event payload `reload` property to trigger analysis request
                 * @param {Object} change
                 * @private
                 */
                onColumnsChange_(change) {
                    switch (change.type) {
                        case 'COLUMNS_UPDATE':
                            this.set('columns', change.payload.currentValue);

                            // TODO This change should be done in a more explicit way
                            TimeSeries.resolveTimeSeries(this.config);

                            this.dispatchAction(change.type, change.payload);

                            const variant = Variants.resolve(_.size(this.columns.getDimensions()), this.get('variant'));

                            const reload = _.get(change, 'payload.reload', true);

                            // Not sure about force pivot as list argument
                            this.setVariant(variant, reload);
                            break;
                        // TODO: VIEW_MODE
                        case 'TIME_SERIES_COLUMN_TOGGLE':
                            const column = _.get(change, 'payload.column', null);
                            const columnIndex = this.columns.getColumnIndex(column);

                            if (columnIndex > -1) {
                                this.dispatchAction('VIEW_MODE_CONFIGURATION_CHANGE', {
                                    path: `columns.${columnIndex}.inTimeSeries`,
                                    currentValue: column.inTimeSeries,
                                    previousValue: !column.inTimeSeries
                                });
                            }
                            break;
                        case 'COLUMN_ADDED':
                        case 'COLUMN_REMOVED':
                        case 'METRIC_COLUMN_UPDATE':
                            this.trackRequestLock_(change.payload.standAlone);
                            this.activateRequestLock()
                                .buildEmptyResponse()
                                .finally(() => this.dispatchAction(change.type, change.payload));

                            break;
                        case 'METRIC_COLUMNS_REORDERED':
                            this.dispatchAction(change.type, change.payload);
                            break;
                        case 'DIMENSION_COLUMNS_REORDERED':
                            this.trackRequestLock_(change.payload.standAlone);
                            this.resetTimeSeriesValues()
                                .activateRequestLock()
                                .buildEmptyResponse()
                                .finally(() => this.dispatchAction(change.type, change.payload));
                            break;
                        default:
                            this.dispatchAction(change.type, change.payload);
                            break;
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#trackRequestLock_
                 * @description Tracks the entrance of request lock.
                 * @param {boolean} standAlone
                 * @returns {WtAnalysis}
                 * @private
                 */
                trackRequestLock_(standAlone) {
                    if (this.isRequestLockApplicable_ && !this.isRequestLockActivated) {
                        trackingService.action(standAlone ? 'analysis.table.edit' : 'reports.element.table.edit');
                    }
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#initViewModes_
                 * @description Initializes the viewMode instance of the analysis.
                 * @returns {WtAnalysis}
                 * @private
                 */
                initViewModes_() {
                    if (_.hasIn(this.config, 'viewMode')) {
                        const viewModes = _.get(this.config, 'viewMode');
                        this.viewModes_ = new WtAnalysisViewModes(viewModes, this.viewModeOrigin_);

                        this.viewModesSubscription = this.viewModes.changes.subscribe(this.onViewModesChange_.bind(this));

                        this.updateActiveViewMode_();
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#onViewModesChange_
                 * @description Observes the changes on the view mode instance and updates the analysis configuration
                 * @param {Object} viewMode The new viewMode configuration
                 * @returns {Promise}
                 * @private
                 * // TODO Propagate the changes on any view mode event to the analysis state
                 */
                onViewModesChange_({type, payload}) {
                    switch (type) {
                        case this.viewModes.actions.VIEW_MODE_ENDORSED:
                            this.goToFirstPage();

                            break;

                        case this.viewModes.actions.VIEW_MODES_COMPARISON_TYPE_UPDATE:
                        case this.viewModes.actions.VIEW_MODES_COMPARISON_VALUES_UPDATE:
                            this.set(`viewMode.${payload.path}`, payload.currentValue).goToFirstPage(false);

                            if (this.isRequestLockApplicable_) {
                                this.activateRequestLock();
                            }

                            return this.buildEmptyResponse();

                        case this.viewModes.actions.VIEW_MODES_COMPARISON_SORTING_UPDATE:
                            this.set(`viewMode.${payload.path}`, payload.currentValue);

                            if (this.isRequestLockApplicable_) {
                                this.activateRequestLock();
                            }

                            return this.buildEmptyResponse();

                        case this.viewModes.actions.VIEW_MODES_COMPARISON_COLLAPSED_UPDATE:
                            this.set(`viewMode.${payload.path}`, payload.currentValue);
                            break;

                        case this.viewModes.actions.VIEW_MODES_PATH_UPDATE:
                        case this.viewModes.actions.VIEW_MODES_PATH_CHART_STATE_UPDATE:
                            this.set(`viewMode.${payload.path}`, payload.currentValue);
                            break;

                        case this.viewModes.actions.VIEW_MODES_PATH_LENGTH_UPDATE:
                        case this.viewModes.actions.VIEW_MODES_PATH_REVERSE_UPDATE:
                        case this.viewModes.actions.VIEW_MODES_PATH_DISTINCT_UPDATE:
                            this.set(`viewMode.${payload.path}`, payload.currentValue);

                            if (this.canApplyViewMode()) {
                                this.triggerAnalysisRequest();
                            } else {
                                if (this.isRequestLockApplicable_) {
                                    this.activateRequestLock();
                                }

                                this.buildEmptyResponse();
                            }

                            break;

                        case this.viewModes.actions.VIEW_MODE_INIT:
                        case this.viewModes.actions.VIEW_MODES_PATH_DIMENSION_CONTEXT_UPDATE:
                        case this.viewModes.actions.VIEW_MODES_PATH_METRIC_ARRANGE_TYPE_UPDATE:
                            this.set(`viewMode.${payload.path}`, payload.currentValue);

                            if (this.isRequestLockApplicable_) {
                                this.activateRequestLock();
                            }

                            this.buildEmptyResponse();

                            break;

                        default:
                            break;
                    }

                    return $q.resolve();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#updateActiveViewMode_
                 * @description Updates the current view mode active state to be able to identify in the future if the current view mode was
                 * at least once activated. Note that the view mode is considered active when it was at least once applied (edit doesn't count).
                 * @returns {WtAnalysis}
                 * @private
                 */
                updateActiveViewMode_() {
                    // Initializes the active view modes map when necessary
                    if (!_.size(this.activeViewModes_)) {
                        _.forEach(ViewMode.VIEW_MODES, (viewMode) => viewMode && (this.activeViewModes_[viewMode] = false));
                    }

                    const activeViewMode = this.getActiveViewMode();
                    const activeEditState = this.getEditState();

                    // The default view mode does not count
                    if (!activeViewMode) {
                        return this;
                    }

                    // The view mode was already loaded once
                    if (this.activeViewModes_[activeViewMode]) {
                        return this;
                    }

                    // The view mode considered active when loaded at least once in view state (edit state does not count)
                    if (activeEditState) {
                        return this;
                    }

                    this.activeViewModes_[activeViewMode] = true;

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#trackRequestLock_
                 * @description Tracks the entrance of request lock.
                 * @returns {WtAnalysis}
                 * @private
                 */
                trackRequestLock_(standAlone) {
                    if (this.isRequestLockApplicable_ && !this.isRequestLockActivated) {
                        trackingService.action(standAlone ? 'analysis.table.edit' : 'reports.element.table.edit');
                    }
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#toJSON
                 * @description Converts the instance into a serializable object
                 * @returns {Object}
                 */
                toJSON() {
                    return this.getSanitizedData_();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#destroy
                 * @description Method invoked before the analysis is detached from the analyses manager. Allows the analysis instance
                 * to release the resources and perform the cleanup.
                 */
                destroy() {
                    if (this.viewModes_) {
                        this.viewModesSubscription.unsubscribe();
                        this.viewModes_.destroy();
                    }

                    if (this.columns_) {
                        this.columnsSubscription.unsubscribe();
                        this.columns_.destroy();
                    }

                    if (this.changesEmitter_) {
                        this.changesEmitter_.complete();
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#toSnapshot
                 * @description Creates a json representation of the analysis that includes all the auxiliary data to be able to recreate the data seen on the screen
                 * It will include the time filter (that is not part of the toJSON representation)
                 * @TODO merge with getConfigClone as they seem to serve same purpose. To be done when wtAnalysis is ES6ified
                 * @returns {Object}
                 */
                toSnapshot() {
                    const analysis = new (this.constructor)(this.getSanitizedData_());

                    const predefinedContainer = _.isPlainObject(analysis.get('predefinedContainer'))
                        ? _.cloneDeep(analysis.get('predefinedContainer'))
                        : {containers: [], filters: [], combined: false};

                    predefinedContainer.filters = filterUtils.getTimelessFilterList(predefinedContainer.filters);

                    if (this.timeFilter_) {
                        predefinedContainer.filters.unshift(
                            this.getSessionTimeFilter()
                        );
                    }
                    analysis.set('predefinedContainer', predefinedContainer);

                    return analysis.getSanitizedData_();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#toString
                 * @description Stringify the instance
                 * @returns {string}
                 */
                toString() {
                    return JSON.stringify(
                        this.toJSON()
                    );
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isPristine
                 * @description Checks if the analysis was changed since it was initialized
                 * @returns {boolean}
                 */
                isPristine() {
                    // TODO Will always be false as we enrich the analysis configuration in the init phase in addition we always update the columns
                    return _.isEqual(this.pristineData_, this.data_)
                        && this.columns_.isPristine()
                        && this.viewModes_.isPristine();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getSanitizedData_
                 * @description Remove any garbage data added by the frontend
                 * @returns {Object|undefined} Sanitized data ready to be sent to backend
                 * @private
                 */
                getSanitizedData_() {
                    return _.cloneDeep(this.data_);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setDebugEnabled
                 * @description Enables debugging messages to be printed to the console
                 * @param {boolean=} debug Should the debugging messages be printed to the console
                 * @returns {WtAnalysis}
                 */
                setDebugEnabled(debug = true) {
                    this.debugEnabled = Boolean(debug);

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#get
                 * @description Analysis configuration accessor
                 * @param {string} iPath The path within the analysis configuration (see lodash _.get())
                 * @returns {*}
                 */
                get(iPath) {
                    const path = iPath ? String(iPath) : null;
                    return path ? _.get(this.config, path) : null;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#set
                 * @description Analysis data setter
                 * @param {string} iPath The path within the analysis configuration (see lodash _.set())
                 * @param {*} iVal The value to be set
                 * @param {boolean} init Indicates whether the setter is called during an init process or not
                 * @returns {WtAnalysis}
                 */
                set(iPath, iVal, init = false) {
                    const path = iPath ? String(iPath) : null;
                    const previousValue = this.get(path);

                    if (previousValue === iVal) {
                        return this;
                    }

                    if (path) {
                        _.set(this.config, path, iVal);

                        // Notify the analysis change subscribers about the change in the configuration
                        this.dispatchAction('PROPERTY_CHANGE', {
                            reload: false,
                            init: init,
                            path: path,
                            currentValue: iVal,
                            previousValue: previousValue
                        });

                        // Notify on viewMode updates‚
                        // TODO: VIEW_MODE
                        if (WtAnalysisViewModes.isActivationPath(path)) {
                            this.viewModes.endorseViewMode(iVal, this.resolvedViewMode);
                        }

                        if (WtAnalysisViewModes.isConfigPath(path)) {
                            this.dispatchAction('VIEW_MODE_CONFIGURATION_CHANGE', {
                                path: path,
                                previousValue: previousValue,
                                currentValue: iVal
                            });
                        }
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#dispatchAction
                 * @description Emits an event in the analysis changes observable.
                 * The analysis dispatch action method is triggered on analysis and other dependent model change (columns|viewMode|etc)
                 * @param {string} type The event name
                 * @param {Object=} payload The payload of the event
                 * @returns {WtAnalysis}
                 */
                dispatchAction(type, payload = {}) {
                    const actions = this.actions;
                    const columnsActions = this.columns ? this.columns.actions : {};
                    const viewModeActions = this.viewModes ? this.viewModes.actions : {};

                    if (!type || !(_.has(actions, type) || _.has(columnsActions, type) || _.has(viewModeActions, type))) {
                        return this;
                    }

                    this.changesEmitter_.next({type, payload});

                    if (this.debugEnabled) {
                        $log.debug(type, payload);
                    }

                    if (!_.get(payload, 'reload', false)) {
                        return this;
                    }

                    // TODO NOT THE RIGHT PLACE
                    if (this.isRequestLockActivated) {
                        this.buildEmptyResponse();
                        return this;
                    }

                    /**
                     * Schedules a data reload if necessary
                     * All reload triggers in the current tick will be collected, but only one reload will be scheduled
                     */
                    const reloadScheduled = Boolean(_.size(this.analysisRequestTriggers));

                    this.analysisRequestTriggers.push(type);

                    if (!reloadScheduled) {
                        setTimeout(() => {
                            this.triggerAnalysisRequest();
                        });
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getContainers
                 * @description
                 * @param {boolean=} injectSessionTimeFilter Should the session time filter be added to the resulting analysis containers.
                 * Some analyses (eg. report analyses that are configured to use the report time) do not have own time filter, instead they use
                 * the reports filter that is stored on the analysis instance and is not present in the analysis configuration.
                 * @returns {Object}
                 */
                getContainers(injectSessionTimeFilter = true) {
                    const containers = _.cloneDeep(this.get('predefinedContainer'));

                    if (injectSessionTimeFilter) {
                        Filters.insertTimes(containers.filters, this.getSessionTimeFilter());
                    }

                    return containers;
                }

                setSegmentConnectionsAndContainers(segmentConnections, containers, keepTimeFilter, reload = false) {
                    return this.setSegmentConnections_(segmentConnections)
                        .setContainers_(containers, keepTimeFilter, reload)
                        .dispatchAction('FILTERS_CONFIG_CHANGE', {reload});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setContainers
                 * @description Replaces the analysis containers that contain the filters to be applied on the analysis
                 * @param {Object} containers The new containers settings
                 * @param {boolean=} keepTimeFilter Should the current time filter be preserved or discarded
                 * @returns {WtAnalysis}
                 */
                setContainers(containers, keepTimeFilter = false) {
                    this.setContainers_(containers, keepTimeFilter);
                    this.dispatchAction('FILTERS_CONFIG_CHANGE');
                    return this;
                }

                setContainers_(containers, keepTimeFilter = false, reload = true) {
                    if (_.isPlainObject(containers) && _.isArray(containers.filters) && _.isArray(containers.containers)) {
                        const ownTimeFilter = _.cloneDeep(this.getOwnTimeFilter());
                        const predefinedContainer = _.assign({combined: false}, containers);

                        this.goToFirstPage(reload)
                            .set('predefinedContainer', predefinedContainer);

                        if (keepTimeFilter) {
                            this.setOwnTimeFilter(ownTimeFilter);
                        }
                    }
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setSegmentConnections
                 * @description Replaces the segments connections on the analysis
                 * @param {Array} segmentConnections The new segment connections collection
                 * @returns {Array} segmentConnections
                 */
                setSegmentConnections_(segmentConnections) {
                    if (_.isArray(segmentConnections)) {
                        this.set('predefinedSegmentConnections', segmentConnections);
                    }
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isPredefinedAnalysis
                 * @description Is a predefined Webtrekk analysis
                 * @returns {boolean}
                 */
                isPredefinedAnalysis() {
                    return !this.isBookmarkAnalysis() && !this.isReportAnalysis() && !this.isPageDetailAnalysis();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isBookmarkAnalysis
                 * @description Is a custom analysis
                 * @returns {boolean}
                 */
                isBookmarkAnalysis() {
                    return Boolean(this.config.customAnalysis);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isPageDetailAnalysis
                 * @description Is a page detail analysis
                 * @returns {boolean}
                 */
                isPageDetailAnalysis() {
                    return Boolean(this.config.pageDetailAnalysis);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isReportAnalysis
                 * @description Is a report analysis
                 * @returns {boolean}
                 */
                isReportAnalysis() {
                    return !this.isPageDetailAnalysis() && Boolean(this.data_.configJson);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isProcessAnalysis
                 * @description Identifies if the analysis is an instance of process analysis. The process analyses have a unique funnel chart.
                 * @returns {boolean}
                 */
                isProcessAnalysis() {
                    return (this.get('standardChart') === 'PROCESS');
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isCohortAnalysis
                 * @description Identifies if the analysis is a ocohort analysis. Simply proxies cohortUtils method.
                 * @returns {boolean}
                 */
                isCohortAnalysis() {
                    return cohortUtils.isCohortAnalysis(this.get('columns'));
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isVideoEngagementAnalysis
                 * @description Identifies if the analysis is an instance of video engagement analysis
                 * @returns {boolean}
                 */
                isVideoEngagementAnalysis() {
                    return (this.get('id') === 'video_engagement');
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isTrendAnalysis
                 * @description Informs if the current analysis is time based (main dimension is time related)
                 * @returns {boolean}
                 */
                isTrendAnalysis() {
                    if (!this.columns) {
                        // KPI|Sparkline implementation pending
                        return false;
                    }

                    if (this.isVariantPivotAsList() || this.isCohortAnalysis()) {
                        return false;
                    }

                    if (this.isTimeSeriesViewMode()) {
                        return true;
                    }

                    const mainDimension = _.head(this.columns.getDimensions());

                    return Boolean(_.get(mainDimension, 'timeFormat'));
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getSessionTimeFilter
                 * @description Returns the session time filter
                 * @returns {Object|undefined}
                 */
                getSessionTimeFilter() {
                    return _.cloneDeep(this.timeFilter_);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setSessionTimeFilter
                 * @description Adds a session based time filter. It is not the analysis configuration time filter.
                 * The difference between the session based time filter and the analysis time filter is that the session based time filter is not persisted on analysis save.
                 * @param {Object} newTimeFilter
                 * @param {Object} reload
                 * @returns {WtAnalysis}
                 */
                setSessionTimeFilter(newTimeFilter, reload = true) {
                    if (Filters.isTime(newTimeFilter) && !_.isEqual(this.timeFilter_, newTimeFilter)) {
                        const currentTimeFilter = this.timeFilter_;

                        this.timeFilter_ = newTimeFilter;

                        if (this.columns) {
                            const sessionLimits = this.columns.getSessionLimits();
                            // Passes custom event to avoid double loading on dimension change of live analyses.
                            this.columns
                                .setSessionLimits(this.get('variant'), sessionLimits[0], sessionLimits[1], false)
                                .setTimeDimensionLimit(this.timeFilter_, reload);
                        }

                        this.dispatchAction('SESSION_TIME_FILTER_CHANGE', {
                            reload,
                            currentValue: newTimeFilter,
                            previousValue: currentTimeFilter
                        });
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getOwnTimeFilter
                 * @description Will return the custom time filter that may or may not be set in the analysis list of filters
                 * @returns {Object|undefined}
                 */
                getOwnTimeFilter() {
                    const filtersList = this.get('predefinedContainer.filters');
                    return filterUtils.getTimeFilterFromList(filtersList);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setOwnTimeFilter
                 * @description Adds a time filter to the analysis filters list. Replaces the existing time filter if applicable.
                 * @param {Object} filter The time filter
                 * @returns {WtAnalysis}
                 */
                setOwnTimeFilter(filter) {
                    const filtersList = this.get('predefinedContainer.filters');
                    Filters.insertTimes(filtersList, filter);

                    if (this.columns) {
                        this.columns.setTimeDimensionLimit(this.getOwnTimeFilter());
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#removeOwnTimeFilter
                 * @description Will remove the time filter from the analysis filter list
                 * @returns {Array} The list of removed filters
                 */
                removeOwnTimeFilter() {
                    const filtersList = this.get('predefinedContainer.filters');
                    return Filters.deleteTimes(filtersList);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#resetFilters
                 * @description Resets the analysis filters
                 * @returns {WtAnalysis}
                 */
                resetFilters() {
                    if (!this.get('predefinedContainer')) {
                        return this;
                    }

                    this.set('predefinedContainer.filters', []);
                    this.set('predefinedContainer.containers', []);

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getActiveViewMode
                 * @description Retrieves the active view mode
                 * @returns {string}
                 * TODO: VIEW_MODE
                 */
                getActiveViewMode() {
                    return _.get(this.get('viewMode'), 'active', ViewMode.VIEW_MODES.DEFAULT);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setActiveViewMode
                 * @description Sets the active view mode
                 * @param {string} iViewMode The new view mode title
                 * @returns {WtAnalysis}
                 * TODO: VIEW_MODE
                 */
                setActiveViewMode(iViewMode) {
                    const currentValue = String(iViewMode);
                    const previousValue = this.getActiveViewMode();
                    const previousEditState = this.getEditState();

                    if (_.includes(ViewMode.VIEW_MODES, currentValue)) {
                        // Important: Firstly resolve the edit state so it does not trigger edit state change event that will reload the analysis data
                        if (!this.isViewModeValid(currentValue)) {
                            // If the new view mode is not configured jump into configuration mode
                            this.setEditState(true, currentValue);
                        }

                        this.set('viewMode.active', currentValue);

                        this.updateActiveViewMode_();
                    }

                    if (previousValue !== currentValue) {
                        //@todo: Do it right dude :)
                        //@todo: Never let promises hanging in the air
                        if (iViewMode === viewModeComparison.VIEW_MODE_NAME && this.isComparisonApplicable) {
                            if (!this.viewModes.comparison.isActiveComparisonApplicable) {
                                this.viewModes.comparison
                                    .initComparisonValues(this.getSessionTimeFilter())
                                    .then(() => {
                                        const reload = false;
                                        return this.activateRequestLock()
                                            .buildEmptyResponse()
                                            .finally(() => this.dispatchAction('VIEW_MODE_SWITCH', {reload, currentValue, previousValue}));
                                    });
                                return this;
                            }
                        }

                        if (!this.canApplyViewMode()) {
                            //@todo: Never let promises hanging in the air
                            this.activateRequestLock()
                                .abortRequests()
                                .buildEmptyResponse()
                                .finally(() => this.dispatchAction('VIEW_MODE_SWITCH', {reload: false, currentValue, previousValue}));
                            return this;
                        }

                        if (this.isRequestLockActivated) {
                            this.deactivateRequestLock().triggerAnalysisRequest();
                        }

                        const reload = !this.isViewModeSignatureCompatible(previousValue, previousEditState);
                        this.dispatchAction('VIEW_MODE_SWITCH', {reload, currentValue, previousValue});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isViewModeSignatureCompatible
                 * @description Compares a view mode signature to the current analysis view mode signature and checks if both view modes
                 * would generate the same query
                 * @param {string|null} viewMode The view mode. If not provided checks against the last query view mode.
                 * @param {boolean|null} editState The edit state. If not provided checks against the last query edit state.
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isViewModeSignatureCompatible(viewMode = null, editState = null) {
                    const signature = [];

                    if (_.isNull(viewMode) || _.isNull(editState)) {
                        signature.push(...requestObjectFactory.getLatestAnalysisQueryViewModeSignature(this.get('id')));
                    } else {
                        signature.push(viewMode, editState);
                    }

                    return ViewMode.areViewModeQueriesCompatible(
                        signature[0],
                        signature[1],
                        this.getActiveViewMode(),
                        this.getEditState()
                    );
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getViewModeConfiguration
                 * @description Retrieves the view mode configuration based on the view mode title.
                 * If no title provided will retrieve the configuration of the active view mode.
                 * Returns null if the view mode does not have a configuration
                 * @param {string=} iViewMode The view mode to retrieve configuration for
                 * @returns {null|Object}
                 * TODO: VIEW_MODE
                 */
                getViewModeConfiguration(iViewMode) {
                    const viewModeTitle = (iViewMode ? iViewMode : this.getActiveViewMode());
                    const viewModeConfigurations = this.get('viewMode');

                    return _.get(viewModeConfigurations, viewModeTitle, null);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isViewModeValid
                 * @description Validates if the active view mode has all required values set up
                 * @param {string} iViewMode View mode
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isViewModeValid(iViewMode) {
                    const viewMode = _.isUndefined(iViewMode) ? this.getActiveViewMode() : iViewMode;

                    if (viewMode === ViewMode.VIEW_MODES.TIME_SERIES) {
                        const timeSeriesConfiguration = this.getViewModeConfiguration(viewMode);

                        return Boolean(
                            // Has selected metrics
                            this.getTimeSeriesColumns().length

                            // Has selected dimensions
                            && (
                                timeSeriesConfiguration.viewType === 'values'
                                    ? timeSeriesConfiguration.values.length
                                    : timeSeriesConfiguration.top
                            )

                            // Has selected time interval
                            && timeSeriesConfiguration.timeArrangeType
                        );
                    }

                    return true;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getEditState
                 * @description Retrieves the edit state of the provided view mode. Will retrieve the edit state of the active view mode if no view mode provided
                 * @param {string=} iViewMode The view mode title
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                getEditState(iViewMode) {
                    const viewModeConfiguration = this.getViewModeConfiguration(iViewMode);

                    return _.get(viewModeConfiguration, 'editState', false);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setEditState
                 * @description Sets the analysis edit state (relevant for view mode configurations of the analysis).
                 * If no view mode provided, will set the edit state of the active view mode.
                 * If the edit state is irrelevant for the view mode, no action will be applied.
                 * Triggers the EDIT_STATE_SWITCH event only when the current view mode state was changed
                 * @param {boolean} iIsEditState The new edit state
                 * @param {string=} iViewMode The view mode title to apply the fix to
                 * @returns {WtAnalysis}
                 * TODO: VIEW_MODE
                 */
                setEditState(iIsEditState, iViewMode) {
                    const activeViewMode = this.getActiveViewMode();
                    const currentValue = Boolean(iIsEditState);
                    const viewMode = iViewMode ? iViewMode : activeViewMode;
                    const isActiveViewMode = (viewMode === activeViewMode);
                    const previousValue = this.getEditState(viewMode);

                    // Performs the validation if the current view mode allows the requested table state
                    if (!this.isEditStateApplicable(viewMode)) {
                        return this;
                    }

                    // If the edit state applied but the view mode is not fully setup (in other words invalid view mode settings applied),
                    // then default to the default view mode
                    if (!currentValue && isActiveViewMode && !this.isViewModeValid(activeViewMode)) {
                        // It is imperative to stop the execution at this point as the view mode setting will take care of updating the state
                        return this.setDefaultViewMode();
                    }

                    // Changes the state of the analysis
                    this.set(['viewMode', viewMode, 'editState'].join('.'), currentValue);

                    this.updateActiveViewMode_();

                    // Notifies other components only if the edit state changed for the active view mode
                    if (previousValue !== currentValue && isActiveViewMode) {
                        const reload = !this.isViewModeSignatureCompatible(viewMode, previousValue);
                        this.dispatchAction('EDIT_STATE_SWITCH', {reload, currentValue, previousValue});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isEditStateApplicable
                 * @description Checks if the view mode supports configuration (edit state) where the user can configure the view mode
                 * @param {string=} iViewMode The view mode to validate the applicability of edit state against
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isEditStateApplicable(iViewMode) {
                    const viewMode = _.isUndefined(iViewMode) ? this.getActiveViewMode() : iViewMode;
                    let editStateApplicable = true;

                    // Default view mode does not have edit (configuration) state
                    if (viewMode === '') {
                        editStateApplicable = false;
                    }

                    return editStateApplicable;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isDefaultViewMode
                 * @description Identifies if the current analysis is configured to be presented in default view mode
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isDefaultViewMode() {
                    return (this.getActiveViewMode() === ViewMode.VIEW_MODES.DEFAULT);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setDefaultViewMode
                 * @description Sets the active view mode of the analysis to default
                 * @returns {WtAnalysis}
                 * TODO: VIEW_MODE
                 */
                setDefaultViewMode() {
                    return this.setActiveViewMode(ViewMode.VIEW_MODES.DEFAULT);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getLayer
                 * @description Will retrieve the monad pool - a.k.a. layer - from which to fish monads - a.k.a.
                 * dimensions and metrics - to valid and further construct
                 * @returns {string}
                 */
                getLayer() {
                    return Monads.getConfigLayer(this.toJSON());
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getViewModeColumns
                 * @description Returns a list of viewMode specific columns.
                 * @returns {Array<Object>}
                 * @todo : Apply to all viewModes apart from path.
                 */
                getViewModeColumns() {
                    return this.viewModes.getColumns(this.getActiveViewMode(), this.columns);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#canApplyViewMode
                 * @description Determines if the view mode configuration is valid for query building or not.
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                canApplyViewMode() {
                    return this.viewModes.canApplyViewMode(this.getActiveViewMode(), this.columns.toJSON());
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isTimeSeriesViewMode
                 * @description Identifies if the current analysis is configured to be presented as timeSeries
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isTimeSeriesViewMode() {
                    const dimensionsCount = this.columns ? this.columns.getDimensions().length : 0;
                    const isTimeSeries = (this.getActiveViewMode() === ViewMode.VIEW_MODES.TIME_SERIES);

                    // Just in case the current analysis was modified from list to pivot
                    // @todo: This place does not seem to be the right place for this check
                    if (dimensionsCount > 1 && isTimeSeries) {
                        this.setDefaultViewMode();
                    }

                    return (this.getActiveViewMode() === ViewMode.VIEW_MODES.TIME_SERIES);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isPathViewMode
                 * @description Identifies if the current analysis is configured to be presented as path
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isPathViewMode() {
                    return this.getActiveViewMode() === ViewMode.VIEW_MODES.PATH;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isComparisonViewMode
                 * @description Identifies if the current analysis is configured to be presented as comparison
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isComparisonViewMode() {
                    return this.getActiveViewMode() === ViewMode.VIEW_MODES.COMPARISON;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isTimeSeriesViewState
                 * @description Determines if the current analysis is in the view - not edit - state of the time series mode.
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isTimeSeriesViewState() {
                    return this.isTimeSeriesViewMode() && !this.getEditState();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setTimeSeriesViewMode
                 * @description Sets the active view mode of the analysis to time series
                 * @returns {WtAnalysis}
                 * TODO: VIEW_MODE
                 */
                setTimeSeriesViewMode() {
                    return this.setActiveViewMode(ViewMode.VIEW_MODES.TIME_SERIES);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#timeSeriesSelectTopX
                 * @description Changes time series view type to "TOP", i.e. the system automatically computes the top
                 * @returns {WtAnalysis}
                 * TODO: VIEW_MODE
                 */
                timeSeriesSelectTopX() {
                    this.set('viewMode.timeSeries.viewType', 'top');

                    if (!this.get('viewMode.timeSeries.top')) {
                        this.set('viewMode.timeSeries.top', 1);
                    }

                    if (this.columns) {
                        this.columns.updateTimeSeriesTopSorting();
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#timeSeriesSelectSpecific
                 * @description Changes time series view type to "VALUES", i.e. the user can freely select the dimensions to be used in the time series
                 * @returns {WtAnalysis}
                 * TODO: VIEW_MODE
                 */
                timeSeriesSelectSpecific() {
                    this.set('viewMode.timeSeries.viewType', 'values');

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getTimeSeriesLimit
                 * @description Returns the max product of selected time series dimensions and metrics
                 * @returns {number}
                 * TODO: VIEW_MODE
                 */
                getTimeSeriesLimit() {
                    const accountLimit = accountService.getConfig('max_analysis_cols');
                    const defaultLimit = 15;
                    return accountLimit || defaultLimit;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isTimeSeriesLimitReached
                 * @description Identifies if the maximum number of elements for a time series is reached. The max elements count is the product of selected metrics and dimensions.
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                isTimeSeriesLimitReached() {
                    const selectedDimensionsCount = this.getTimeSeriesDimensionsCount();
                    const selectedMetricsCount = this.getTimeSeriesColumns().length;

                    return (selectedDimensionsCount || 1) * (selectedMetricsCount || 1) >= this.getTimeSeriesLimit();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getTimeSeriesDimensionsCount
                 * @description Get time series dimensions count based on the time series view type
                 * @returns {number}
                 * TODO: VIEW_MODE
                 */
                getTimeSeriesDimensionsCount() {
                    const isValuesViewType = (this.get('viewMode.timeSeries.viewType') === 'values');
                    return (isValuesViewType
                            ? this.get('viewMode.timeSeries.values').length
                            : this.get('viewMode.timeSeries.top')
                    );
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#canRemoveTimeSeriesMetric
                 * @description Can a metric be unselected from time series (at least one metric should be present in time series)
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                canRemoveTimeSeriesMetric() {
                    const selectedMetricsCount = this.getTimeSeriesColumns().length;

                    return selectedMetricsCount > 1;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#canAddTimeSeriesMetric
                 * @description Can a new metric be added to time series
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                canAddTimeSeriesMetric() {
                    const selectedMetricsCount = this.getTimeSeriesColumns().length;
                    const selectedDimensionsCount = this.getTimeSeriesDimensionsCount();

                    return Boolean(this.getTimeSeriesLimit() >= selectedDimensionsCount * (selectedMetricsCount + 1));
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#canAddTimeSeriesDimension
                 * @description Can a new dimension be added to time series
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                canAddTimeSeriesDimension() {
                    const selectedMetricsCount = this.getTimeSeriesColumns().length || 1;
                    const selectedDimensionsCount = this.getTimeSeriesDimensionsCount();

                    return Boolean(this.getTimeSeriesLimit() >= (selectedDimensionsCount + 1) * selectedMetricsCount);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getTimeSeriesColumns
                 * @description Fetches the metric columns added to time series
                 * @returns {Array}
                 * TODO: VIEW_MODE
                 */
                getTimeSeriesColumns() {
                    if (!this.columns) {
                        return [];
                    }

                    return _.filter(this.columns.getMetrics(), {inTimeSeries: true});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#resetTimeSeriesValues
                 * @description Resets the list of time series values
                 * @returns {WtAnalysis}
                 * TODO: VIEW_MODE
                 */
                resetTimeSeriesValues() {
                    this.set('viewMode.timeSeries.values', []);

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#toggleTimeSeriesValue
                 * @description Toggles a dimension value in the time series values list
                 * @param {*} value The dimension value to be displayed as time series
                 * @returns {boolean} Indicator that the value was toggled
                 * TODO: VIEW_MODE
                 */
                toggleTimeSeriesValue(value) {
                    const timeSeriesValuePath = 'viewMode.timeSeries.values';

                    const valueIndex = _.indexOf(this.get(timeSeriesValuePath), value);
                    const limitReached = this.isTimeSeriesLimitReached();
                    const hasSelectedDimensions = this.get(timeSeriesValuePath).length > 0;

                    if (valueIndex > -1) {
                        const values = this.get(timeSeriesValuePath);

                        values.splice(valueIndex, 1);

                        // Clone the original array to trigger the changes emitter
                        this.set(timeSeriesValuePath, values.slice());

                        return true;
                    } else if (!limitReached || !hasSelectedDimensions) {
                        const values = this.get(timeSeriesValuePath);

                        values.push(value);

                        // Clone the original array to trigger the changes emitter
                        this.set(timeSeriesValuePath, values.slice());

                        return true;
                    }

                    return false;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getTimeSeriesTopValue
                 * @description Gets the time series top value
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                getTimeSeriesTopValue() {
                    return this.get('viewMode.timeSeries.top');
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setTimeSeriesTopValue
                 * @description Sets the time series top value
                 * @param {number} iValue The time series top value
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                setTimeSeriesTopValue(iValue) {
                    const value = Number(iValue);

                    if (!value) {
                        return false;
                    }

                    this.set('viewMode.timeSeries.top', value);
                    if (this.isTimeSeriesLimitReached()) {
                        const selectedMetricsCount = this.getTimeSeriesColumns().length;
                        this.set('viewMode.timeSeries.top', Math.floor(this.getTimeSeriesLimit() / selectedMetricsCount));
                    }

                    return true;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getComparisonAliasGroups
                 * @description Will return the alias groups per parent alias for a comparion view mode analysis.
                 * @returns {Object}
                 * TODO: VIEW_MODE
                 */
                getComparisonAliasGroups() {
                    const replicationFactor = this.viewModes.comparison.activeReplicationFactor;
                    return Columns.getAliasGroups(this.columns.getMetrics(), replicationFactor);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getTimeSeriesAliasGroups
                 * @description Will iterate over a list of time series columns and return a hash map of each aliases corresponding
                 * list of sub aliases.
                 * @returns {Object} A hash map with parent aliases as keys and lists of sub aliases as values
                 * TODO: VIEW_MODE
                 */
                getTimeSeriesAliasGroups() {
                    if (!this.isTimeSeriesViewMode()) {
                        return {};
                    }

                    const columns = this.getTimeSeriesColumns();
                    const replicationFactor = TimeSeries.getMetricReplicationFactor(this.config);

                    return Columns.getAliasGroups(columns, replicationFactor);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getVisibleTimeSeriesSubAliases
                 * @description Will the list of visible sub aliases
                 * @returns {Array}
                 * TODO: VIEW_MODE
                 */
                getVisibleTimeSeriesSubAliases() {
                    if (!this.isTimeSeriesViewMode()) {
                        return [];
                    }

                    const metrics = this.columns ? this.columns.getMetrics() : [];

                    const visibleAliases = _.chain(metrics)
                        .filter((iColumn) => {
                            return _.get(iColumn, 'chart.visible');
                        })
                        .map('alias')
                        .value();

                    return _.chain(this.getTimeSeriesAliasGroups())
                        .pick(visibleAliases)
                        .values()
                        .flatten()
                        .uniq()
                        .value();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getAliasGroupParent_
                 * @description Will return the parent alias of a alias column given an alias group map.
                 * @param   {string} alias The sub alias whose parent we are looking for
                 * @param   {Object} aliasGroups A key value map with parent aliases and their sub-alias lists
                 * @returns {string|undefined}
                 * @private
                 * TODO: VIEW_MODE
                 */
                getAliasGroupParent_(alias, aliasGroups) {
                    return _.chain(aliasGroups)
                            .pickBy(subAliases => _.includes(subAliases, alias))
                            .keys()
                            .first()
                            .value();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getComparisonParentAlias
                 * @description Will return the parent alias of an alias column of a comparison analysis.
                 * @param   {string} alias The sub alias whose parent we are looking for
                 * @returns {string}
                 * TODO: VIEW_MODE
                 */
                getComparisonParentAlias(alias) {
                    if (!this.isComparisonViewMode()) {
                        return alias;
                    }
                    const aliasGroups = this.getComparisonAliasGroups();
                    return this.getAliasGroupParent_(alias, aliasGroups) || alias;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getTimeSeriesParentAlias
                 * @description Will return the parent alias of a alias column of a time series analyses. If the analyses is not a
                 * time series analyses, the method will plainly return the given alias.
                 * @param  {string} alias - The sub alias whose parent we are looking for
                 * @returns {string}
                 * TODO: VIEW_MODE
                 */
                getTimeSeriesParentAlias(alias) {
                    if (!this.isTimeSeriesViewMode()) {
                        return alias;
                    }
                    const aliasGroups = this.getTimeSeriesAliasGroups();
                    return this.getAliasGroupParent_(alias, aliasGroups) || alias;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getPredefinedQuery
                 * @description Will return a query object with filters and containers based on the the predefinedContainer and predefinedSegmentConnections
                 * settings of an analysis. The returned object can be used as is to construct the whole query (report generation request for
                 * this analysis). If the iTimeFilter flag is set to true the query object will include the time filter too.
                 * @param  {boolean} iTimeFilter - Whether to include the time filter or not
                 * @param  {boolean} iPristine - Whether to construct it based on the pristine config of the analysis
                 * @returns {Object}
                 */
                getPredefinedQuery(iTimeFilter, iPristine) {
                    const query_ = {
                        filters: [],
                        containers: []
                    };

                    // Utilize the current configuration or the pristine one based on the passed flag.
                    const config_ = iPristine ? this.pristineConfig : this.config;

                    // Clone the filter rules
                    _.chain(config_)
                        .get('predefinedContainer.filters')
                        .forEach((iFilter) => {
                            query_.filters.push(_.cloneDeep(iFilter));
                        })
                        .value();

                    // Clone the containers
                    _.chain(config_)
                        .get('predefinedContainer.containers')
                        .forEach((iContainer) => {
                            query_.containers.push(_.cloneDeep(iContainer));
                        })
                        .value();

                    // Convert the segments to containers and push them to the list of containers
                    _.chain(config_)
                        .get('predefinedSegmentConnections')
                        .filter((segmentConnection) => {
                            const segmentID = _.get(segmentConnection, 'segmentID');
                            const segment = segmentService.getActiveSegmentById(segmentID);
                            return !_.isEmpty(segment);
                        })
                        .forEach((iSegmentConnector) => {
                            const container = segmentService.getSegmentConnectorAsContainer(iSegmentConnector);
                            if (!_.isEmpty(container)) {
                                query_.containers.push(container);
                            }
                        })
                        .value();

                    Filters.insertTimes(query_.filters, this.getSessionTimeFilter());

                    return query_;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getConfigClone
                 * @description Will return a clone of the analysis configuration with an empty id, an optional title
                 * and clean list of useless columns, with proper aliases and the limits of the first dimension reset.
                 * @param  {string=} title - An optional title for the new analysis
                 * @param  {boolean=} injectTime - Should the new configuration contain the time filter
                 * @returns {Object}
                 */
                getConfigClone(title = '', injectTime = false) {
                    const columns = this.columns ? this.columns.toJSON() : [];

                    let configClone = _.chain(this.config)
                        .cloneDeep()
                        .set('id', '')
                        .set('title', title || this.get('title'))
                        .set('predefinedSegmentConnections', _.cloneDeep(this.get('predefinedSegmentConnections')))
                        .set('predefinedContainer', _.cloneDeep(this.get('predefinedContainer')))
                        .set('columns', _.cloneDeep(columns))
                        .value();
                    // Clean the columns from useless objects (i.e. nr columns)
                    // re-assign aliases and reset the lower and upper limits of the first dimension
                    // to clean pagination.
                    Columns
                        .removeUseless(configClone.columns)
                        .addAliases(configClone.columns)
                        .resetLimit(configClone.columns, this.getLayer());

                    if (injectTime) {
                        Filters.insertTimes(configClone.predefinedContainer.filters, this.getSessionTimeFilter());
                    }

                    return configClone;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#hasComparisonTimeShifts
                 * @description Will identify if the current analysis if and when in comparison can have time shifted columns.
                 * @returns {boolean}
                 * TODO: VIEW_MODE
                 */
                hasComparisonTimeShifts() {
                    if (!this.isComparisonViewMode()) {
                        return false;
                    }

                    if (this.viewModes.comparison.isSegmentBasedComparison()) {
                        return false;
                    }

                    return timeShiftService.isValidTimeShiftUnit(this.firstDimensionTimeFormat.toUpperCase());
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isTimeAnalysis
                 * @description Will identify if the current analysis is a time based analysis. The first dimennsion
                 * is a time dimension in other words.
                 * @returns {boolean}
                 */
                isTimeAnalysis() {
                    return !_.isEmpty(this.firstDimensionTimeFormat);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setChartType
                 * @description Sets the new chart type for the analysis
                 * @param {string} chartType The chart type. Unfortunately frontend has a different standard of chart types than backend. Can be frontend or backend compatible.
                 * @returns {WtAnalysis}
                 * @throws Error
                 */
                setChartType(chartType) {
                    let standardChart;
                    const CHART_TYPES = WtAnalysis.CHART_TYPES;

                    if (_.includes(CHART_TYPES, chartType)) {
                        standardChart = _.findKey(CHART_TYPES, fChartType => fChartType === chartType);
                    } else if (_.hasIn(CHART_TYPES, chartType)) {
                        standardChart = chartType;
                    }

                    if (!standardChart) {
                        throw Error(`Unknown chart type ${chartType}`);
                    }

                    this.set('standardChart', standardChart);

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getChartTypeByViewMode_
                 * @description Will return the chart type uppercase string based on the current view mode
                 * @returns {string}
                 * TODO: VIEW_MODE
                 */
                getChartTypeByViewMode_() {
                    if (this.isTimeSeriesViewMode() || this.isCohortAnalysis()) {
                        return 'LINE';
                    }

                    if (this.isComparisonViewMode()) {
                        return this.isTimeAnalysis() ? 'LINE' : 'COLUMN';
                    }

                    if (this.isPathViewMode()) {
                        return 'SANKEY';
                    }

                    return this.get('standardChart');
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#getChartType
                 * @description
                 * @param {boolean=} frontendFormat Should the chart type be converted to frontend format
                 * @returns {*}
                 */
                getChartType(frontendFormat = false) {
                    const standardChart = this.getChartTypeByViewMode_();
                    const CHART_TYPES = WtAnalysis.CHART_TYPES;

                    // Returns the original backend format
                    if (!frontendFormat) {
                        return standardChart;
                    }

                    // Returns the converted frontend format
                    return CHART_TYPES[standardChart];
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#showChart
                 * @description Shows the chart of the analysis
                 * @param {boolean} init Indicates whether the method is called during an init process
                 * @returns {WtAnalysis}
                 */
                showChart(init = false) {
                    const previousValue = this.get('showPic');
                    const currentValue = true;

                    if (previousValue !== currentValue) {
                        this.set('showPic', true, init);
                        this.set('forbidChart', false, init); // Just keep it in sync as its value is already poisoned with showPic user settings.

                        this.dispatchAction(this.actions.CHART_VISIBILITY_CHANGED, {currentValue, previousValue});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#hideChart
                 * @description Hides the chart of the analysis
                 * @param {boolean} init Indicates whether the method is called during an init process
                 * @returns {WtAnalysis}
                 */
                hideChart(init = false) {
                    const previousValue = this.get('showPic');
                    const currentValue = false;

                    if (previousValue !== currentValue) {
                        this.set('showPic', false, init);
                        this.set('forbidChart', true, init); // Just keep it in sync as its value is already poisoned with showPic user settings.

                        this.dispatchAction(this.actions.CHART_VISIBILITY_CHANGED, {currentValue, previousValue});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#showTable
                 * @description Shows the table of the analysis
                 * @param {boolean} init Indicates whether the method is called during an init process
                 * @returns {WtAnalysis}
                 */
                showTable(init = false) {
                    const previousValue = this.get('showTable');
                    const currentValue = true;

                    if (previousValue !== currentValue) {
                        this.set('showTable', true, init);

                        this.dispatchAction(this.actions.TABLE_VISIBILITY_CHANGED, {currentValue, previousValue});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#hideTable
                 * @description Hides the table of the analysis
                 * @param {boolean} init Indicates whether the method is called during an init process
                 * @returns {WtAnalysis}
                 */
                hideTable(init = false) {
                    const previousValue = this.get('showTable');
                    const currentValue = false;

                    if (previousValue !== currentValue) {
                        this.set('showTable', false, init);

                        this.dispatchAction(this.actions.TABLE_VISIBILITY_CHANGED, {currentValue, previousValue});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#goToFirstPage
                 * @description Navigates to the first page of the analysis data
                 * @param   {boolean=} reload Flag denoting that the changes require the analysis data to be reloaded
                 * @returns {WtAnalysis}
                 */
                goToFirstPage(reload = true) {
                    if (this.columns_) {
                        this.columns_.goToFirstPage(reload);
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setVariant
                 * @description Changes the analysis variant. Analysis variant impacts the way the data is presented to the end user.
                 * @param {string} iVariant The new analysis variant
                 * @param {boolean=} reload
                 * @returns {WtAnalysis}
                 */
                setVariant(iVariant, reload = true) {
                    const dimensions = this.columns ? this.columns.getDimensions() : [];
                    const previousValue = this.get('variant');
                    const currentValue = Variants.resolve(_.size(dimensions), iVariant);

                    if (previousValue === currentValue) {
                        return this;
                    }

                    this.set('variant', currentValue);

                    this.updateSessionLimits();

                    this.dispatchAction('VARIANT_CHANGE', {reload, currentValue, previousValue});

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#updateSessionLimits
                 * @description Updates the columns limits based on the current analysis configuration.
                 * Some scenarios will override user configured limits, eg. pivot as list will override the users limits configured for the
                 * first dimension
                 * @returns {WtAnalysis}
                 */
                updateSessionLimits() {
                    if (this.columns) {
                        const sessionLimits = this.columns.getSessionLimits();
                        this.columns.setSessionLimits(this.get('variant'), sessionLimits[0], sessionLimits[1], false);
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isVariantPivot
                 * @description Checks if the current variant is pivot
                 * @returns {boolean}
                 */
                isVariantPivot() {
                    return this.get('variant') === 'PIVOT';
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isVariantList
                 * @description Checks if the current variant is list
                 * @returns {boolean}
                 */
                isVariantList() {
                    return this.get('variant') === 'LIST';
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#isVariantPivotAsList
                 * @description Checks if the current variant is pivot as list
                 * @returns {boolean}
                 */
                isVariantPivotAsList() {
                    return this.get('variant') === 'PIVOT_AS_LIST';
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#setPredefinedTableRows
                 * @description Enforces with table limit on the first dimension
                 * @param {Object} lowerLimit The lower limit of the first dimension
                 * @param {Object} upperLimit The upper limit of the first dimension
                 * TODO Listen for the setColumnLimits event and sync the predefinedTableRows
                 * when column limits update triggered not by this method
                 * @returns {WtAnalysis}
                 */
                setPredefinedTableRows(lowerLimit, upperLimit) {
                    if (!_.isNumber(lowerLimit) || !_.isNumber(upperLimit) || lowerLimit < 1 || lowerLimit > upperLimit || !this.columns) {
                        return this;
                    }

                    this.set('predefinedTableRows', (upperLimit - lowerLimit + 1));

                    this.columns.setColumnLimits(this.columns.getFirstDimension(), lowerLimit, upperLimit);

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#scalePathHeight
                 * @description Increases the vertical number of nodes displayed in the path chart by simply increasing
                 * the upperLimit of the session limits by given step.
                 * @param   {number} step
                 * @returns {WtAnalysis}
                 */
                scalePathHeight(step = 5) {
                    this.columns.stepUpSessionLimits(step);
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysis#scalePathLength
                 * @description Increases the horizontal number of nodes displayed in the path chart by simply increasing
                 * the pathLength of the view mode configuration instance.
                 * @returns {WtAnalysis}
                 */
                scalePathLength() {
                    if (this.isPathViewMode()) {
                        this.viewModes.path.scalePathLength();
                    }

                    return this;
                }
            };
        }]);
})(window.angular, window._, window.URL);
